import { useDispatch, useSelector } from "react-redux";
import ColorizeIcon from "@mui/icons-material/Colorize";
import { selectUsedProps } from "../../../app/slices/PropLibrarySlice";
import usePropHelper from "../../../hooks/PropHelper";
import { SequenceNodeProps } from "./SequenceNodeProps";
import useUpdateSequenceInteraction from "./UpdateSequenceInteraction.hook";
import { useMemo } from "react";
import {
  clickIndicatorStyles,
  IClickIndicator,
} from "../../../models/interactions/ClickIndicator";
import NodeAllPropsDropdown from "../components/NodeAllPropsDropdown";
import NodeStringDropdown from "../components/NodeStringDropdown";
import {
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../../app/slices/GlobalSlice";
import { applyPickedPropToInteraction } from "./SequencePropPicker.helpers";

export default function ClickIndicatorNodePreview(props: SequenceNodeProps) {
  const propInteraction = props.interaction as IClickIndicator;
  const { getUsedPropByInstanceId } = usePropHelper();
  const usedProps = useSelector(selectUsedProps);
  const { updateInteraction } = useUpdateSequenceInteraction();
  const dispatch = useDispatch();
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);

  const usedProp = useMemo(() => {
    if (!propInteraction.propGuid) return null;
    return getUsedPropByInstanceId(propInteraction.propGuid);
  }, [propInteraction.propGuid, usedProps]);

  const isPickerActiveForNode =
    sequencePositionPicker.isOpen &&
    sequencePositionPicker.nodeId === props.nodeId &&
    sequencePositionPicker.positionType === "prop";

  const propDropdown = useMemo(
    () => (
      <NodeAllPropsDropdown
        label="Prop"
        selectedPropStateId={propInteraction.propGuid}
        accessory={
          <button
            type="button"
            aria-label="Pick on map"
            title="Pick on map"
            data-testid="pick-prop-on-map-button"
            className={
              "group flex h-[32px] w-[32px] items-center justify-center rounded-full border-[2px] transition-all duration-150 " +
              (isPickerActiveForNode
                ? "border-creator-primary bg-creator-primary text-white shadow-[0_0_0_4px_rgba(58,196,255,0.18)]"
                : "border-creator-primary bg-creator-primary-background text-creator-primary hover:bg-creator-primary-washed")
            }
            onClick={() =>
              dispatch(
                openSequencePositionPicker({
                  nodeId: props.nodeId,
                  positionType: "prop",
                  title: "Pick prop",
                  presentation: "splitView",
                }),
              )
            }
          >
            <ColorizeIcon fontSize="inherit" className="text-[18px]" />
          </button>
        }
        onClick={(propGuid) => {
          const prop = { id: propGuid } as any;
          updateInteraction(props.nodeId, {
            ...applyPickedPropToInteraction(
              propInteraction,
              prop,
              getUsedPropByInstanceId,
            ),
          } as IClickIndicator);
        }}
      />
    ),
    [
      dispatch,
      getUsedPropByInstanceId,
      isPickerActiveForNode,
      propInteraction,
      props.nodeId,
      updateInteraction,
    ]
  );

  const tagDropdown = useMemo(
    () => (
      <NodeStringDropdown
        items={usedProp?.metadata.clickableTags.map((p) => p.name) ?? []}
        selectedItem={propInteraction.tag}
        label="Tag"
        onClick={(tag) => {
          updateInteraction(props.nodeId, {
            ...propInteraction,
            tag: tag,
          } as IClickIndicator);
        }}
      />
    ),
    [usedProp, propInteraction.propGuid, updateInteraction, props.nodeId]
  );

  const stylesDropdown = useMemo(
    () => (
      <NodeStringDropdown
        items={clickIndicatorStyles}
        selectedItem={propInteraction.style}
        label="Style"
        onClick={(style) => {
          updateInteraction(props.nodeId, {
            ...propInteraction,
            style: style,
          } as IClickIndicator);
        }}
      />
    ),
    [usedProp, propInteraction.propGuid, updateInteraction, props.nodeId]
  );

  return (
    <div className="flex flex-col w-full h-full gap-[12px] noDrag">
      {propDropdown}
      {tagDropdown}
      {stylesDropdown}
    </div>
  );
}
